<template>
  <div class="chat-window-container">
    <div class="chat-header">
      <div class="header-left">
        <button v-if="isMobile" @click="$emit('back')" class="back-btn">
            <i class="bi bi-arrow-left"></i>
        </button>
        <div class="chat-avatar" :class="{ 'has-photo': chat?.user_photo }">
          <img v-if="chat?.user_photo" :src="getPhotoUrl(chat.user_photo)" :alt="chat?.user_name || 'Usuário'" />
          <div v-else class="avatar-circle">
            <i class="bi bi-person"></i>
          </div>
        </div>
        <div class="chat-info">
          <h5 
            class="chat-name" style="cursor: pointer;"
            :class="{ 'clickable': chat?.id_patient_user }"
            @click="chat?.id_patient_user && showPatientDetails()"
            :title="chat?.id_patient_user ? 'Clique para ver dados do paciente' : ''"
          >
            {{ chat?.user_name || formatPhone(chat?.phone) }}
          </h5>
          <div class="chat-status-wrapper">
            <span class="chat-status-badge" :class="chat?.assigned_to === 'ai' ? 'status-ai' : 'status-user'">
              <i class="bi" :class="chat?.assigned_to === 'ai' ? 'bi-cpu' : 'bi-person'"></i>
              {{ chat?.assigned_to === 'ai' ? 'IA respondendo' : 'Atendimento manual' }}
            </span>
          </div>
        </div>
      </div>
      <div class="header-right">
        <button
          v-if="chat?.assigned_to === 'user'"
          @click="returnToAi"
          class="assign-btn ai-mode me-2"
        >
          <i class="bi bi-cpu"></i>
          Devolver para a IA
        </button>
        <button
          v-if="chat?.assigned_to === 'user'"
          @click="toggleFinalize"
          class="assign-btn finalize-mode"
        >
          <i class="bi bi-check-circle"></i>
          Finalizar Atendimento
        </button>
        <button
          v-if="chat?.assigned_to === 'ai'"
          @click="toggleAssign"
          class="assign-btn ai-mode"
        >
          <i class="bi bi-person"></i>
          Assumir
        </button>
      </div>
    </div>

    <div class="chat-messages" ref="messagesContainer">
      <div
        v-for="(group, index) in messageGroups"
        :key="`group-${index}`"
        class="message-group"
      >
        <div class="date-divider" v-if="group.date">
          {{ formatDate(group.date) }}
        </div>
        <MessageBubble
          v-for="message in group.messages"
          :key="message.id"
          :message="message"
          :is-sent="message.role === 'assistant'"
          @reply="handleReply"
        />
      </div>

      <TypingIndicator :is-typing="isTyping" />
    </div>

    <MessageInput
      ref="messageInput"
      @send="handleSendText"
      @send-media="handleSendMedia"
      @send-audio="handleSendAudio"
      :loading="sending"
      :replying-to="replyingTo"
      :chat="chat"
      :requires-template="requiresTemplate"
      @cancel-reply="replyingTo = null"
    />
  </div>

  <div v-if="showPatientModal" class="modal fade show" style="display: block; z-index: 1055;" tabindex="-1" @click.self="closePatientModal">
    <div class="modal-dialog modal-dialog-centered" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Dados do Paciente</h5>
          <button type="button" class="btn-close" @click="closePatientModal" aria-label="Fechar"></button>
        </div>
        <div class="modal-body" v-if="patientDetails">
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label fw-bold">Nome</label>
              <p class="form-control-plaintext">{{ patientDetails.patient_name || '-' }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">CPF</label>
              <p class="form-control-plaintext">{{ patientDetails.patient_cpf || '-' }}</p>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label fw-bold">Telefone</label>
              <p class="form-control-plaintext">{{ patientDetails.patient_phone || '-' }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Data de Nascimento</label>
              <p class="form-control-plaintext">{{ formatPatientDate(patientDetails.patient_birthdate) || '-' }}</p>
            </div>
          </div>
          <div class="row mb-3" v-if="patientDetails.patient_email">
            <div class="col-md-12">
              <label class="form-label fw-bold">E-mail</label>
              <p class="form-control-plaintext">{{ patientDetails.patient_email }}</p>
            </div>
          </div>
          <div class="row mb-3" v-if="patientDetails.patient_mother_name">
            <div class="col-md-12">
              <label class="form-label fw-bold">Nome da Mãe</label>
              <p class="form-control-plaintext">{{ patientDetails.patient_mother_name }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closePatientModal">Fechar</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showPatientModal" class="modal-backdrop fade show" style="z-index: 1050;"></div>
</template>

<script>
import MessageBubble from './MessageBubble.vue';
import MessageInput from './MessageInput.vue';
import TypingIndicator from './TypingIndicator.vue';
import whatsappHelper from "@/helpers/whatsapp";
import Str from "@/helpers/str";
import http from "@/helpers/http";
import whatsappService from "@/service/whatsappService";
import Alert from "@/helpers/alert";

export default {
  name: 'ChatWindow',
  components: {
    MessageBubble,
    MessageInput,
    TypingIndicator,
  },
  props: {
    chat: {
      type: Object,
      default: null,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    isTyping: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    placeId: {
      type: Number,
      required: true,
    },
  },
  emits: ['send-message', 'assign-chat', 'back', 'chat-updated'],
  data() {
    return {
      sending: false,
      replyingTo: null,
      patientDetails: null,
      showPatientModal: false,
    };
  },
  computed: {
    requiresTemplate() {
      if (!this.chat) return false;
      return this.chat.isWithin24HourWindow === false;
    },
    messageGroups() {
      const groups = [];
      let currentGroup = null;

      this.messages.forEach(message => {
        const date = new Date(message.created_at).toDateString();
        
        if (!currentGroup || currentGroup.date !== date) {
          currentGroup = {
            date,
            messages: [],
          };
          groups.push(currentGroup);
        }

        currentGroup.messages.push(message);
      });

      return groups;
    },
  },
  watch: {
    'chat.id': {
      handler() {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.clearInput();
        }
      },
      immediate: false,
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
    isTyping() {
      if (this.isTyping) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
  },
  methods: {
    formatPhone(phone) {
      return whatsappHelper.formatPhone(phone);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Hoje';
      }
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Ontem';
      }

      return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    async toggleAssign() {
      if (!this.chat || this.chat.assigned_to !== 'ai') return;

      const result = await Swal.fire({
        title: 'Assumir atendimento',
        text: 'Deseja assumir este atendimento? A IA deixará de responder automaticamente.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, assumir',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        this.$emit('assign-chat', this.chat.id, 'user');
      }
    },
    async returnToAi() {
      if (!this.chat || this.chat.assigned_to !== 'user') return;

      const result = await Swal.fire({
        title: 'Devolver para a IA',
        text: 'O assistente voltará a responder neste chat. Deseja continuar?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, devolver',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        try {
          await whatsappService.assignChat(this.chat.id, this.placeId, 'ai');
          Alert.success('Chat devolvido para a IA.');
          this.$emit('chat-updated');
        } catch (error) {
          console.error('Error returning chat to AI:', error);
          Alert.error(error.response?.data?.message || 'Erro ao devolver para a IA');
        }
      }
    },
    async toggleFinalize() {
      if (!this.chat || this.chat.assigned_to !== 'user') return;

      const result = await Swal.fire({
        title: 'Finalizar atendimento',
        text: 'Deseja finalizar este atendimento? O chat voltará para a IA e uma nova mensagem do paciente iniciará um novo fluxo.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, finalizar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        try {
          await whatsappService.finalizeChat(this.placeId, this.chat.id);
          Alert.success('Atendimento finalizado com sucesso!');
          this.$emit('chat-updated');
        } catch (error) {
          console.error('Error finalizing chat:', error);
          Alert.error(error.response?.data?.error || 'Erro ao finalizar atendimento');
        }
      }
    },
    handleReply(message) {
      this.replyingTo = message;
      this.$refs.messageInput?.focus();
    },
    async handleSendText(payload) {
      if (!this.chat) return;
      this.sending = true;
      
      const content = typeof payload === 'string' ? payload : payload.content;
      const templateId = payload?.template_id || null;
      const templateParameters = payload?.template_parameters || [];
      const replyToMessageId = this.replyingTo?.additional_data?.message_id || null;
      await this.$emit('send-message', this.chat.id, { 
        content, 
        type: 'text', 
        reply_to_message_id: replyToMessageId,
        template_id: templateId,
        template_parameters: templateParameters,
      });
      this.replyingTo = null;
      this.sending = false;
    },
    async handleSendMedia(fileData, type) {
      if (!this.chat) return;
      this.sending = true;
      const file = fileData?.file || fileData;
      const fileName = fileData?.name || file?.name;
      const mediaType = type || fileData?.type || (file?.type ? whatsappHelper.getMediaType(file.type) : 'document');
      await this.$emit('send-message', this.chat.id, { file, fileName, type: mediaType });
      this.sending = false;
    },
    async handleSendAudio(audioData) {
      if (!this.chat) return;
      this.sending = true;
      const replyToMessageId = this.replyingTo?.additional_data?.message_id || null;
      await this.$emit('send-message', this.chat.id, { audioData, type: 'audio', reply_to_message_id: replyToMessageId });
      this.replyingTo = null;
      this.sending = false;
    },
    getPhotoUrl(photo) {
      if (!photo) return null;
      return Str.url(photo);
    },
    async showPatientDetails() {
      if (!this.chat?.id_patient_user) return;

      try {
        const response = await http.get(`/users/${this.chat.id_patient_user}/patients?main=true`);
        const patients = response.data;
        
        if (patients && patients.length > 0) {
          this.patientDetails = patients[0];
          this.showPatientModal = true;
        } else {
          Swal.fire({
            icon: 'info',
            title: 'Paciente não encontrado',
            text: 'Não foi possível encontrar os dados do paciente.',
          });
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.response?.data?.message || 'Não foi possível carregar os dados do paciente.',
        });
      }
    },
    closePatientModal() {
      this.showPatientModal = false;
      this.patientDetails = null;
    },
    formatPatientDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },
  },
}
</script>

<style scoped>
.chat-window-container {
  display: flex;
  flex-direction: column;
  background: #f5f8fa;
  overflow: hidden;
  height: 100%;
}

.chat-header {
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  color: white;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.back-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
}

.chat-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  font-size: 1.3rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex-shrink: 0;
}

.chat-avatar.has-photo {
  background: transparent;
  padding: 0;
}

.chat-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-circle {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-info {
  flex: 1;
  min-width: 0;
}

.chat-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: white;
}

.chat-name.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.chat-name.clickable:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.chat-status-wrapper {
  margin-top: 4px;
}

.chat-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.chat-status-badge.status-ai {
  background: rgba(37, 211, 102, 0.2);
  border: 1px solid rgba(37, 211, 102, 0.3);
}

.chat-status-badge.status-user {
  background: rgba(255, 193, 7, 0.2);
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.chat-status-badge i {
  font-size: 0.7rem;
}

.header-right {
  display: flex;
  gap: 8px;
}

.assign-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  backdrop-filter: blur(10px);
}

.assign-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.assign-btn.ai-mode {
  background: rgba(37, 211, 102, 0.25);
  border-color: rgba(37, 211, 102, 0.4);
}

.assign-btn.ai-mode:hover {
  background: rgba(37, 211, 102, 0.35);
}

.assign-btn.user-mode {
  background: rgba(255, 193, 7, 0.25);
  border-color: rgba(255, 193, 7, 0.4);
}

.assign-btn.user-mode:hover {
  background: rgba(255, 193, 7, 0.35);
}

.assign-btn.finalize-mode {
  background: #28a745;
  color: white;
  border: none;
}

.assign-btn.finalize-mode:hover {
  background: #218838;
  color: white;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f5f8fa;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(120, 15, 90, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(37, 211, 102, 0.03) 0%, transparent 50%);
}

.message-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-divider {
  text-align: center;
  margin: 20px 0;
  color: #5e6278;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  position: relative;
}

.date-divider::before,
.date-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 30%;
  height: 1px;
  background: linear-gradient(to right, transparent, #e4e6ef, transparent);
}

.date-divider::before {
  left: 0;
}

.date-divider::after {
  right: 0;
}
</style>

